import { Tabs, TabsContent, TabsList, TabsTrigger } from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { TreasuryPage } from "../../../features/treasury/shared.js";
import { StatementImportTab } from "../../../features/treasury/statements/import.js";
import { StatementQueueTab } from "../../../features/treasury/statements/queue.js";
import { MatchingRulesTab } from "../../../features/treasury/statements/rules.js";
import { StatementTemplatesTab } from "../../../features/treasury/statements/templates.js";

const TABS = ["import", "queue", "templates", "rules"] as const;
type TabKey = (typeof TABS)[number];

const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/finans/ekstre")({
  validateSearch: (search) => searchSchema.parse(search),
  component: BankStatementsPage,
});

function BankStatementsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const tab: TabKey = Route.useSearch().tab ?? "import";

  return (
    <TreasuryPage title={t("menu.bankStatements")} description={t("treasury.statements.description")}>
      {({ companyId }) => (
        <Tabs
          value={tab}
          onValueChange={(next) =>
            void navigate({
              search: (previous) => ({ company: previous.company, tab: next as TabKey }),
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("menu.bankStatements")} className="flex-wrap">
            {TABS.map((key) => (
              <TabsTrigger key={key} value={key}>
                {t(`treasury.statements.tabs.${key}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="import">
            {tab === "import" ? <StatementImportTab companyId={companyId} /> : null}
          </TabsContent>
          <TabsContent value="queue">
            {tab === "queue" ? <StatementQueueTab companyId={companyId} /> : null}
          </TabsContent>
          <TabsContent value="templates">
            {tab === "templates" ? <StatementTemplatesTab /> : null}
          </TabsContent>
          <TabsContent value="rules">
            {tab === "rules" ? <MatchingRulesTab companyId={companyId} /> : null}
          </TabsContent>
        </Tabs>
      )}
    </TreasuryPage>
  );
}
